/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Turnstile } from '@marsidev/react-turnstile';

interface BasketFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  message: string;
  captchaToken: string;
  website: string;
}

interface FormProps {
  formData: BasketFormData;
  setFormData: React.Dispatch<React.SetStateAction<BasketFormData>>;
  handleSubmit: () => Promise<void>;
  disabled?: boolean;
}

const validationSchema = yup.object({
  firstName: yup
    .string()
    .trim()
    .min(2, 'Please enter a valid name')
    .max(50, 'Please enter a valid name')
    .required('Please specify your first name'),
  lastName: yup
    .string()
    .trim()
    .min(2, 'Please enter a valid name')
    .max(50, 'Please enter a valid name')
    .required('Please specify your last name'),
  email: yup
    .string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email is required.'),
  company: yup
    .string()
    .trim()
    .min(1, 'Please enter a valid company name')
    .max(50, 'Please enter a valid company name'),
  message: yup
    .string()
    .trim()
    .max(1000, 'The message cannot contain more than 1000 characters')
    .required('Message is required'),
});

const Form = ({ formData, setFormData, handleSubmit }: FormProps) => {
  const localStorageKey = 'formData';
  const { t } = useTranslation();
  const [captchaToken, setCaptchaToken] = useState(
    formData?.captchaToken || '',
  );
  const [captchaError, setCaptchaError] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const initialValues =
    JSON.parse(localStorage.getItem(localStorageKey) as string) || formData;

  const formik = useFormik<BasketFormData>({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: async () => {
      if (!captchaToken) {
        setCaptchaError('Please verify that you are not a robot.');
        return;
      }

      await handleSubmit();
      formik.resetForm();
      setCaptchaToken('');
      setCaptchaKey((current) => current + 1);
    },
  });

  useEffect(() => {
    setFormData({ ...formik.values, captchaToken });
    localStorage.setItem(localStorageKey, JSON.stringify(formik.values));
  }, [captchaToken, formik.values, setFormData]);

  useEffect(() => {
    if (!turnstileSiteKey) {
      setCaptchaError('Captcha is not configured. Please contact support.');
      return;
    }

    setCaptchaError('');
  }, [turnstileSiteKey]);

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 3 }}>
      <Box
        sx={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        <TextField
          label="Website"
          name="website"
          autoComplete="off"
          tabIndex={-1}
          value={formik.values.website || ''}
          onChange={formik.handleChange}
        />
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="firstName"
            name="firstName"
            label={t('basket.form.firstName')}
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            sx={{
              '& .MuiInputBase-root': {
                height: '56px',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="lastName"
            name="lastName"
            label={t('basket.form.lastName')}
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
            sx={{
              '& .MuiInputBase-root': {
                height: '56px',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label={t('basket.form.email')}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            sx={{
              '& .MuiInputBase-root': {
                height: '56px',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="company"
            name="company"
            label={t('basket.form.company')}
            value={formik.values.company}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.company && Boolean(formik.errors.company)}
            helperText={formik.touched.company && formik.errors.company}
            sx={{
              '& .MuiInputBase-root': {
                height: '56px',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="message"
            name="message"
            label={t('form.message')}
            multiline
            rows={4}
            value={formik.values.message}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.message && Boolean(formik.errors.message)}
            helperText={formik.touched.message && formik.errors.message}
            sx={{
              '& .MuiInputBase-root': {
                minHeight: '160px',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          {turnstileSiteKey && (
            <Box sx={{ marginBottom: '8px' }}>
              <Turnstile
                key={captchaKey}
                siteKey={turnstileSiteKey}
                onSuccess={(token) => {
                  setCaptchaToken(token);
                  setCaptchaError('');
                }}
                onExpire={() => {
                  setCaptchaToken('');
                  setCaptchaError('Captcha expired. Please verify again.');
                }}
                onError={() => {
                  setCaptchaToken('');
                  setCaptchaError('Captcha failed to load. Please try again.');
                }}
              />
            </Box>
          )}
          {captchaError && (
            <Typography color="error" variant="body2">
              {captchaError}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Form;
